import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  catchError,
  finalize,
  firstValueFrom,
  Observable,
  of,
  switchMap,
  tap,
  throwError,
  timeout,
} from 'rxjs';
import { AUTH_ENDPOINTS } from '@core/config/api.config';
import { ApiErrorService } from '@core/http/api-error.service';
import { TokenStorageService } from '@core/services/token-storage.service';
import { AuthStore } from './auth.store';
import { AuthResponse, AuthenticatedUser, LoginRequest } from './auth.types';

const REQUEST_TIMEOUT_MS = 15000;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly authStore = inject(AuthStore);
  private readonly apiError = inject(ApiErrorService);

  login(credentials: LoginRequest): Observable<AuthenticatedUser> {
    this.authStore.setLoading(true);

    return this.http.post<AuthResponse>(AUTH_ENDPOINTS.login, credentials).pipe(
      timeout(REQUEST_TIMEOUT_MS),
      switchMap((response) => {
        this.tokenStorage.setToken(response.accessToken);
        return this.loadAuthenticatedUser();
      }),
      tap((user) => this.authStore.setAuthenticatedUser(user)),
      catchError((error: unknown) => {
        this.tokenStorage.clearToken();
        this.authStore.clearSession();
        return throwError(() => this.apiError.normalize(error));
      }),
      finalize(() => this.authStore.setLoading(false)),
    );
  }

  loadAuthenticatedUser(): Observable<AuthenticatedUser> {
    return this.http.get<AuthenticatedUser>(AUTH_ENDPOINTS.me).pipe(timeout(REQUEST_TIMEOUT_MS));
  }

  hasPermission(permission: string): boolean {
    const user = this.authStore.user();
    if (!user) return false;
    if (this.isSuperAdmin()) return true;

    return user.permissoes.includes(permission);
  }

  isSuperAdmin(): boolean {
    return this.authStore.isSuperAdmin();
  }

  async restoreSession(): Promise<void> {
    const token = this.tokenStorage.getToken();

    if (!token) {
      this.authStore.finishSessionRestore(null);
      return;
    }

    await firstValueFrom(
      this.loadAuthenticatedUser().pipe(
        tap((user) => {
          if (!user.superAdmin && !user.role && (!user.acessos || user.acessos.length === 0)) {
            this.logout();
            return;
          }
          this.authStore.finishSessionRestore(user);
        }),
        catchError(() => {
          this.tokenStorage.clearToken();
          this.authStore.finishSessionRestore(null);
          return of(null);
        }),
      ),
    );
  }

  logout(): void {
    this.tokenStorage.clearToken();
    this.authStore.clearSession();
    void this.router.navigateByUrl('/login');
  }

  endSessionAfterUnauthorized(): void {
    this.tokenStorage.clearToken();
    this.authStore.clearSession();
  }
}
